import { createSelector } from '@reduxjs/toolkit';

import { type Clip, ClipType, VisibleColumnType } from '@air/api/types';
import { ClipUtils } from '@air/utils-clips';

import { uploadsForCurrentBoardSelector } from '~/store/centralizedBoard/selectors';
import { convertUploadToKanbanItemData } from '~/store/kanbanManager/utils';
import { boardIdsBeingDeletedSelector } from '~/store/tasks/selectors';
import { type AirState } from '~/store/types';
import { DndItemType, type DndSortableKanbanItemData } from '~/types/DndKit';

const baseKanbanManagerSelector = ({ kanbanManager }: AirState) => kanbanManager;

const passedIdSelector = (_: AirState, id: string) => id;

const kanbanColumnSelector = createSelector(
  [baseKanbanManagerSelector, boardIdsBeingDeletedSelector, passedIdSelector],
  (kanbanManager, excludedBoardIds, columnId) => {
    const column = kanbanManager.columns[columnId];

    if (column && excludedBoardIds.length > 0) {
      return {
        ...column,
        items: column.items.filter(
          (item) => item.dndType !== DndItemType.kanbanBoard || !excludedBoardIds.includes(item.itemId),
        ),
      };
    }

    return column;
  },
);

export const kanbanColumnItemsSelector = createSelector(
  [baseKanbanManagerSelector, uploadsForCurrentBoardSelector, boardIdsBeingDeletedSelector, passedIdSelector],
  (kanbanManager, uploads, excludedBoardIds, columnId) => {
    const items =
      kanbanManager.columns[columnId]?.items.filter(
        (item) => item.dndType !== DndItemType.kanbanBoard || !excludedBoardIds.includes(item.itemId),
      ) || [];

    if (columnId === VisibleColumnType.unassignedCustomFieldValue) {
      const finishedClipIds = new Set(
        items
          .filter((item) => item.dndType !== DndItemType.kanbanBoard)
          .filter((item) => {
            const clip = item.apiData.data as Clip;
            // Non-media files don't transcode, so treat them as finished immediately
            return clip.type === ClipType.nonMedia || ClipUtils.isTranscodeFinished(clip.status);
          })
          .map((item) => (item.apiData.data as Clip).id),
      );
      const filteredUploads = uploads.filter((u) => !finishedClipIds.has(u.clipId));
      const activeClipIds = new Set(filteredUploads.map((u) => u.clipId));
      const filteredItems = items.filter(
        (item) => item.dndType === DndItemType.kanbanBoard || !activeClipIds.has((item.apiData.data as Clip).id),
      );

      return [...filteredUploads.map(convertUploadToKanbanItemData), ...filteredItems];
    }

    return items;
  },
);
export const kanbanColumnNeedsToLoadItemsSelector = createSelector([kanbanColumnSelector], (column) => !column?.items);

export const kanbanColumnSortableKeysSelector = createSelector(kanbanColumnItemsSelector, (kanbanItems) =>
  kanbanItems.map((item) => item.sortableKanbanItemKey),
);

export const findKanbanItemByClipIdInColumnSelector = createSelector(
  [baseKanbanManagerSelector, passedIdSelector],
  (kanbanManager, clipId) => {
    let item: DndSortableKanbanItemData | undefined;
    let columnId: string | undefined;
    for (const [columnKey, column] of Object.entries(kanbanManager.columns)) {
      item = column.items.find((item) => (item.apiData.data as Clip).id === clipId);
      if (item) {
        columnId = columnKey;
        break;
      }
    }
    const columnItems = columnId ? kanbanManager.columns[columnId].items : undefined;

    return {
      columnId,
      columnItems,
      item,
    };
  },
);

export const findKanbanItemByAssetIdInColumnSelector = createSelector(
  [baseKanbanManagerSelector, passedIdSelector],
  (kanbanManager, assetId) => {
    let item: DndSortableKanbanItemData | undefined;
    let columnId: string | undefined;
    for (const [columnKey, column] of Object.entries(kanbanManager.columns)) {
      item = column.items.find((item) => (item.apiData.data as Clip).assetId === assetId);
      if (item) {
        columnId = columnKey;
        break;
      }
    }
    const columnItems = columnId ? kanbanManager.columns[columnId].items : undefined;

    return {
      columnId,
      columnItems,
      item,
    };
  },
);

export const findKanbanItemInColumnSelector = createSelector(
  [baseKanbanManagerSelector, passedIdSelector],
  (kanbanManager, itemId) => {
    let item: DndSortableKanbanItemData | undefined;
    let columnId: string | undefined;
    for (const [columnKey, column] of Object.entries(kanbanManager.columns)) {
      item = column.items.find((item) => item.itemId === itemId);
      if (item) {
        columnId = columnKey;
        break;
      }
    }
    const columnItems = columnId ? kanbanManager.columns[columnId].items : undefined;

    return {
      columnId,
      columnItems,
      item,
    };
  },
);

const uploadsCountForColumnSelector = createSelector(
  [passedIdSelector, uploadsForCurrentBoardSelector],
  (columnId, uploads) => {
    return columnId === VisibleColumnType.unassignedCustomFieldValue ? uploads.length : 0;
  },
);

export const kanbanColumnItemCountSelector = createSelector(
  [kanbanColumnSelector, uploadsCountForColumnSelector],
  (column, uploadsCount) => {
    if (column) {
      return column.totalItems + uploadsCount;
    }
  },
);

export const kanbanColumnUnsyncedItemsSelector = createSelector(
  [kanbanColumnSelector],
  (column) => column?.unsyncedItems,
);
